import { addMonths, addYears, startOfDay } from 'date-fns';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import {
  COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2,
  DEFAULT_MIN_STRING_LENGTH,
  EMAIL_MAX_LENGTH,
  ENGLISH_SCORE_VALUE_DEFINITION_MAP,
  ENGLISH_TEST_TYPE_VALUES,
  EXPECTED_ARRIVAL_DATE_MINIMUM_MONTHS,
  EXPECTED_GRADUATION_DATE_MINIMUM_YEARS,
  FIELD_OF_STUDY_VALUES,
  FULL_COUNTRY_VALUES,
  MAX_ESTIMATED_LOAN_AMOUNT,
  MIN_ESTIMATED_LOAN_AMOUNT,
  STUDENT_FULL_NAME_MAX_LENGTH,
} from './constants';
import { EnglishExamScores, EnglishTest } from './types';

export const SCHOOL_NAME_REGEX = /^$|^[^<>@:~#%^*$\\{}|=]+$/g;
export const PROGRAM_NAME_REGEX = SCHOOL_NAME_REGEX;

export const studentFirstNameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('loans.errors.studentFirstNameRequired'),
    })
    .trim()
    .min(DEFAULT_MIN_STRING_LENGTH, t('loans.errors.studentFirstNameRequired'))
    .max(
      STUDENT_FULL_NAME_MAX_LENGTH,
      t('loans.errors.maxLength', {
        fieldName: t('loans.fieldLabels.studentFirstName'),
        maxLength: STUDENT_FULL_NAME_MAX_LENGTH,
      }),
    );

export const studentLastNameSchema = (t: TFunction) =>
  z
    .string({
      invalid_type_error: t('loans.errors.valid', {
        fieldName: t('loans.fieldLabels.studentLastName'),
      }),
    })
    .trim()
    .max(
      STUDENT_FULL_NAME_MAX_LENGTH,
      t('loans.errors.maxLength', {
        fieldName: t('loans.fieldLabels.studentLastName'),
        maxLength: STUDENT_FULL_NAME_MAX_LENGTH,
      }),
    )
    .optional();

export const studentEmailAddressSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('loans.errors.emailRequired'),
    })
    .email(
      t('loans.errors.valid', {
        fieldName: t('loans.fieldLabels.studentEmailAddress'),
      }),
    )
    .min(DEFAULT_MIN_STRING_LENGTH, t('loans.errors.emailRequired'))
    .max(
      EMAIL_MAX_LENGTH,
      t('loans.errors.maxLength', {
        fieldName: t('loans.fieldLabels.studentEmailAddress'),
        maxLength: EMAIL_MAX_LENGTH,
      }),
    );

export const studentEmailAddressConfirmationSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('loans.errors.confirmEmailRequired'),
    })
    .trim()
    .email(
      t('loans.errors.valid', {
        fieldName: t('loans.fieldLabels.studentEmailAddressConfirmation'),
      }),
    )
    .min(DEFAULT_MIN_STRING_LENGTH, t('loans.errors.confirmEmailRequired'))
    .max(
      EMAIL_MAX_LENGTH,
      t('loans.errors.maxLength', {
        fieldName: t('loans.fieldLabels.studentEmailAddressConfirmation'),
        maxLength: EMAIL_MAX_LENGTH,
      }),
    );

export const contactPhoneNumberSchema = (t: TFunction) =>
  z.string({
    required_error: t('loans.errors.phoneNumberRequired'),
  });

export const nationalitySchema = (t: TFunction) =>
  z.enum(FULL_COUNTRY_VALUES, {
    required_error: t('loans.errors.nationalityRequired'),
  });

export const destinationSchema = (t: TFunction) =>
  z.enum(FULL_COUNTRY_VALUES, {
    required_error: t('loans.errors.destinationRequired'),
  });

export const fieldOfStudySchema = (t: TFunction) =>
  z.enum(FIELD_OF_STUDY_VALUES, {
    required_error: t('loans.errors.fieldOfStudyRequired'),
  });

export const englishExamTypeSchema = (t: TFunction) =>
  z.union(
    [z.enum(ENGLISH_TEST_TYPE_VALUES as unknown as readonly [string, ...string[]]), z.literal('')],
    {
      invalid_type_error: t('loans.errors.valid', {
        fieldName: t('loans.educationSummary.englishTestType'),
      }),
    },
  );

export const schoolNameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('loans.errors.schoolNameRequired'),
    })
    .trim()
    .min(DEFAULT_MIN_STRING_LENGTH, t('loans.errors.schoolNameRequired'))
    .regex(SCHOOL_NAME_REGEX, t('common.errors.specialCharactersNotAllowed'));

export const programNameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('loans.errors.programNameRequired'),
    })
    .trim()
    .min(DEFAULT_MIN_STRING_LENGTH, t('loans.errors.programNameRequired'))
    .regex(PROGRAM_NAME_REGEX, t('common.errors.specialCharactersNotAllowed'));

export const estimatedLoanAmountSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('loans.errors.estimatedLoanAmountRequired'),
      invalid_type_error: t('loans.errors.valid', {
        fieldName: t('loans.fieldLabels.estimatedLoanAmount'),
      }),
    })
    .refine(
      value =>
        Number(value) >= MIN_ESTIMATED_LOAN_AMOUNT && Number(value) <= MAX_ESTIMATED_LOAN_AMOUNT,
      t('loans.errors.estimatedLoanAmountValidNumber'),
    );

export const termsAndConditionsSchema = (t: TFunction) =>
  z.boolean({
    invalid_type_error: t('loans.agreementAndConsent.errors.termsAndConditionsRequired'),
  });

export const consentToUsePrivateDataSchema = (t: TFunction) =>
  z.boolean({
    invalid_type_error: t('loans.agreementAndConsent.errors.consentToUsePrivateDataRequired'),
  });

export const loansV2AgreementAndConsentSchema = (t: TFunction) =>
  z.object({
    agreeTermsAndCondition: termsAndConditionsSchema(t),
    consentToUsePrivateData: consentToUsePrivateDataSchema(t),
  });

export const expectedArrivalDateSchema = (t: TFunction) => {
  const isAtLeastThreeMonthsAhead = (dateString: string) => {
    const date = new Date(dateString);
    const threeMonthsAhead = addMonths(new Date(), EXPECTED_ARRIVAL_DATE_MINIMUM_MONTHS);

    const formatter = new Intl.DateTimeFormat('en-CA');

    const formattedDate = formatter.format(date);
    const formattedThreeMonthsAhead = formatter.format(threeMonthsAhead);

    return startOfDay(new Date(formattedDate)) >= startOfDay(new Date(formattedThreeMonthsAhead));
  };

  return z
    .string({
      required_error: t('loans.errors.fieldRequired', {
        fieldName: t('loans.fieldLabels.expectedArrivalDate'),
      }),
      invalid_type_error: t('loans.errors.valid', {
        fieldName: t('loans.fieldLabels.expectedArrivalDate'),
      }),
    })
    .refine(dateStr => isAtLeastThreeMonthsAhead(dateStr), {
      message: t('loans.errors.expectedArrivalDateRange', {
        monthMinimum: EXPECTED_ARRIVAL_DATE_MINIMUM_MONTHS,
      }),
    });
};

export const expectedGraduationDateSchema = (t: TFunction) => {
  const isAtLeastOneYearAhead = (dateString: string) => {
    const date = new Date(dateString);
    const oneYearAhead = addYears(new Date(), EXPECTED_GRADUATION_DATE_MINIMUM_YEARS);
    return startOfDay(date) >= startOfDay(oneYearAhead);
  };

  return z
    .string({
      required_error: t('loans.errors.fieldRequired', {
        fieldName: t('loans.fieldLabels.expectedGraduationDate'),
      }),
      invalid_type_error: t('loans.errors.valid', {
        fieldName: t('loans.fieldLabels.expectedGraduationDate'),
      }),
    })
    .refine(dateStr => isAtLeastOneYearAhead(dateStr), {
      message: t('loans.errors.expectedGraduationDateRange', {
        fieldName: t('loans.fieldLabels.expectedGraduationDate'),
      }),
    });
};

export const dateOfExamSchema = (t: TFunction) => {
  return z
    .string({
      required_error: t('loans.educationSummary.errors.dateOfExamRequired'),
    })
    .refine(dateStr => new Date(dateStr).getTime() <= Date.now(), {
      message: t('loans.errors.dateInThePast'),
    });
};

export const ScoreSchema = (
  min: number,
  max: number,
  minError: string,
  maxError: string,
  step = 1,
) =>
  z
    .string()
    .trim()
    .transform(val => (val === '' ? undefined : Number(val)))
    .optional()
    .refine(num => num === undefined || num >= min, { message: minError })
    .refine(num => num === undefined || num <= max, { message: maxError })
    .refine(num => num === undefined || num % step === 0, {
      message: `Value must be in increments of ${step}`,
    });

export const RankSchema = (t: TFunction, examType: string) =>
  ScoreSchema(
    0,
    100,
    t('loans.errors.minRank', {
      examType,
      minRank: 0,
    }),
    t('loans.errors.maxRank', {
      examType,
      maxRank: 100,
    }),
  );

export const PTEScoreSchema = (t: TFunction) =>
  ScoreSchema(
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.PTE.min,
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.PTE.max,
    t('loans.errors.minScore', {
      examType: EnglishTest.PTE,
      minScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.PTE.min,
    }),
    t('loans.errors.maxScore', {
      examType: EnglishTest.PTE,
      maxScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.PTE.max,
    }),
  );

export const DuolingoOverallSchema = (t: TFunction) =>
  ScoreSchema(
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.Duolingo.min,
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.Duolingo.max,
    t('loans.errors.minScore', {
      examType: EnglishTest.DUOLINGO,
      minScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.Duolingo.min,
    }),
    t('loans.errors.maxScore', {
      examType: EnglishTest.DUOLINGO,
      maxScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.Duolingo.max,
    }),
  );

export const ToeflScoreSchema = (t: TFunction) =>
  ScoreSchema(
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.TOEFL.min,
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.TOEFL.max,
    t('loans.errors.minScore', {
      examType: EnglishTest.TOEFL,
      minScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.TOEFL.min,
    }),
    t('loans.errors.maxScore', {
      examType: EnglishTest.TOEFL,
      maxScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.TOEFL.max,
    }),
  );

export const ToeflOverallSchema = (t: TFunction) =>
  ScoreSchema(
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.TOEFL.min,
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.TOEFL.maxOverall,
    t('loans.errors.minScore', {
      examType: EnglishTest.TOEFL,
      minScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.TOEFL.min,
    }),
    t('loans.errors.maxScore', {
      examType: EnglishTest.TOEFL,
      maxScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.TOEFL.maxOverall,
    }),
  );

export const IeltsScoreSchema = (t: TFunction) =>
  ScoreSchema(
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.IELTS.min,
    ENGLISH_SCORE_VALUE_DEFINITION_MAP.IELTS.max,
    t('loans.errors.minScore', {
      examType: EnglishTest.IELTS,
      minScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.IELTS.min,
    }),
    t('loans.errors.maxScore', {
      examType: EnglishTest.IELTS,
      maxScore: ENGLISH_SCORE_VALUE_DEFINITION_MAP.IELTS.max,
    }),
    0.5,
  );

export const GmatAnalyticalWritingSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.AnalyticalWriting.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.AnalyticalWriting.max,
    t('loans.errors.minScore', {
      examType: 'GMAT - AWA',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.AnalyticalWriting.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GMAT - AWA',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.AnalyticalWriting.max,
    }),
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.AnalyticalWriting.step,
  );

export const GmatVerbalReasoningSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.VerbalReasoning.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.VerbalReasoning.max,
    t('loans.errors.minScore', {
      examType: 'GMAT - Verbal',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.VerbalReasoning.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GMAT - Verbal',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.VerbalReasoning.max,
    }),
  );

export const GmatQuantitativeReasoningSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.QuantitativeReasoning.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.QuantitativeReasoning.max,
    t('loans.errors.minScore', {
      examType: 'GMAT - Quantitative',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.QuantitativeReasoning.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GMAT - Quantitative',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.QuantitativeReasoning.max,
    }),
  );

export const GmatDataInsightsSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.DataInsights.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.DataInsights.max,
    t('loans.errors.minScore', {
      examType: 'GMAT - Data Insights',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.DataInsights.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GMAT - Data Insights',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.DataInsights.max,
    }),
  );

export const GmatIntegratedReasoningSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.IntegratedReasoning.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.IntegratedReasoning.max,
    t('loans.errors.minScore', {
      examType: 'GMAT - Integrated Reasoning',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.IntegratedReasoning.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GMAT - Integrated Reasoning',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.IntegratedReasoning.max,
    }),
  );

export const GmatTotalScoreSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.OverallScore.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.OverallScore.max,
    t('loans.errors.minScore', {
      examType: 'GMAT - Total',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.OverallScore.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GMAT - Total',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GMAT.OverallScore.max,
    }),
  );

export const GmatRankSchema = (t: TFunction) => RankSchema(t, 'GMAT');

export const GreRankSchema = (t: TFunction) => RankSchema(t, 'GRE');

export const GreAnalyticalWritingSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.AnalyticalWriting.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.AnalyticalWriting.max,
    t('loans.errors.minScore', {
      examType: 'GRE - Analytical Writing',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.AnalyticalWriting.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GRE - Analytical Writing',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.AnalyticalWriting.max,
    }),
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.AnalyticalWriting.step,
  );

export const GreVerbalReasoningSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.VerbalReasoning.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.VerbalReasoning.max,
    t('loans.errors.minScore', {
      examType: 'GRE - Verbal',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.VerbalReasoning.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GRE - Verbal',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.VerbalReasoning.max,
    }),
  );

export const GreQuantitativeReasoningSchema = (t: TFunction) =>
  ScoreSchema(
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.QuantitativeReasoning.min,
    COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.QuantitativeReasoning.max,
    t('loans.errors.minScore', {
      examType: 'GRE - Quantitative',
      minScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.QuantitativeReasoning.min,
    }),
    t('loans.errors.maxScore', {
      examType: 'GRE - Quantitative',
      maxScore: COMPETITIVE_TESTS_VALUE_DEFINITION_MAP_V2.GRE.QuantitativeReasoning.max,
    }),
  );

export const IELTSScoresSchema = (t: TFunction) =>
  z.object({
    englishReadingScore: IeltsScoreSchema(t),
    englishWritingScore: IeltsScoreSchema(t),
    englishListeningScore: IeltsScoreSchema(t),
    englishSpeakingScore: IeltsScoreSchema(t),
    englishOverallScore: IeltsScoreSchema(t),
  });

export const TOEFLScoresSchema = (t: TFunction) =>
  z.object({
    englishReadingScore: ToeflScoreSchema(t),
    englishWritingScore: ToeflScoreSchema(t),
    englishListeningScore: ToeflScoreSchema(t),
    englishSpeakingScore: ToeflScoreSchema(t),
    englishOverallScore: ToeflOverallSchema(t),
  });

export const PTEScoresSchema = (t: TFunction) =>
  z.object({
    englishReadingScore: PTEScoreSchema(t),
    englishWritingScore: PTEScoreSchema(t),
    englishListeningScore: PTEScoreSchema(t),
    englishSpeakingScore: PTEScoreSchema(t),
    englishOverallScore: PTEScoreSchema(t),
  });

export const DuolingoScoresSchema = (t: TFunction) =>
  z.object({
    englishOverallScore: DuolingoOverallSchema(t),
  });

export const GMATScoresSchema = (t: TFunction) =>
  z.discriminatedUnion('hasGmat', [
    z
      .object({
        hasGmat: z.literal(true),
      })
      .merge(
        z.object({
          gmatDateOfExam: dateOfExamSchema(t),
          gmatTotalScore: GmatTotalScoreSchema(t),
          gmatTotalRank: GmatRankSchema(t),
          gmatVerbalScore: GmatVerbalReasoningSchema(t),
          gmatVerbalRank: GmatRankSchema(t),
          gmatQuantitativeScore: GmatQuantitativeReasoningSchema(t),
          gmatQuantitativeRank: GmatRankSchema(t),
          gmatAwaScore: GmatAnalyticalWritingSchema(t),
          gmatAwaRank: GmatRankSchema(t),
          gmatDataInsightsScore: GmatDataInsightsSchema(t),
          gmatDataInsightsRank: GmatRankSchema(t),
          gmatIntegratedReasoningScore: GmatIntegratedReasoningSchema(t),
          gmatIntegratedReasoningRank: GmatRankSchema(t),
        }),
      ),
    z.object({ hasGmat: z.literal(false) }),
  ]);

export const GREScoresSchema = (t: TFunction) =>
  z.discriminatedUnion('hasGre', [
    z
      .object({
        hasGre: z.literal(true),
      })
      .merge(
        z.object({
          greDateOfExam: dateOfExamSchema(t),
          greVerbalScore: GreVerbalReasoningSchema(t),
          greVerbalRank: GreRankSchema(t),
          greQuantitativeScore: GreQuantitativeReasoningSchema(t),
          greQuantitativeRank: GreRankSchema(t),
          greAnalyticalWritingScore: GreAnalyticalWritingSchema(t),
          greAnalyticalWritingRank: GreRankSchema(t),
        }),
      ),
    z.object({ hasGre: z.literal(false) }),
  ]);

export const BaseEducationSummarySchema = (t: TFunction) =>
  z.object({
    englishTestType: englishExamTypeSchema(t),
    hasGmat: z.boolean(),
    hasGre: z.boolean(),
  });

export const FileInfoSchema = (t: TFunction) =>
  z
    .object({
      id: z.string(),
      file: z.object({
        name: z.string(),
      }),
      errorMessage: z.string().optional(),
    })
    .refine(
      value => !value.errorMessage,
      t('loans.errors.valid', {
        fieldName: t('loans.fieldLabels.file'),
      }),
    );

const createEnglishExamSchema = (
  testType: EnglishTest,
  scoreSchema: z.AnyZodObject,
  t: TFunction,
): z.AnyZodObject =>
  z
    .object({
      englishTestType: z.literal(testType),
      englishDateOfExam: dateOfExamSchema(t),
      englishTestScoreDocument: FileInfoSchema(t).array().length(1),
    })
    .merge(scoreSchema);

export const EnglishExamSchema = (t: TFunction) =>
  z.preprocess(
    val => {
      const examScores = val as EnglishExamScores;
      if (examScores.englishTestType !== '') {
        return examScores;
      }
      return null;
    },
    z.union([
      z.null(),
      z.discriminatedUnion('englishTestType', [
        createEnglishExamSchema(EnglishTest.IELTS, IELTSScoresSchema(t), t),
        createEnglishExamSchema(EnglishTest.TOEFL, TOEFLScoresSchema(t), t),
        createEnglishExamSchema(EnglishTest.PTE, PTEScoresSchema(t), t),
        createEnglishExamSchema(EnglishTest.DUOLINGO, DuolingoScoresSchema(t), t),
      ]),
    ]),
  );

export const EducationSummarySchema = (t: TFunction) =>
  BaseEducationSummarySchema(t)
    .and(EnglishExamSchema(t))
    .and(GMATScoresSchema(t))
    .and(GREScoresSchema(t));
